import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Monitor, Users, ArrowRight, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const Solutions = () => {
  const { t } = useLanguage();

  const solutions = [
    {
      icon: Smartphone,
      title: t('solutions.app.title'),
      description: t('solutions.app.description'),
      features: ['iOS & Android', 'QR Code Access', 'Instant Download'],
      highlight: true,
      pricing: '20% commission'
    },
    {
      icon: Monitor,
      title: t('solutions.kiosk.title'),
      description: t('solutions.kiosk.description'),
      features: ['Touchscreen Display', 'Custom Branding', 'Self-Service'],
      highlight: false,
      pricing: '20% commission'
    },
    {
      icon: Users,
      title: t('solutions.staffing.title'),
      description: t('solutions.staffing.description'),
      features: ['Pro Photographers', 'Training Available', 'Flexible Options'],
      highlight: false,
      pricing: '20% commission'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section 
      id="solutions" 
      data-testid="solutions-section"
      className="py-24 md:py-32 relative overflow-hidden bg-[#0A0A0A]"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-[#FF7A00]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm tracking-widest uppercase mb-4">
            Solutions
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('solutions.title')}
          </h2>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            {t('solutions.subtitle')}
          </p>
        </motion.div>

        {/* Stand iFotolife Showcase */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="glass-card rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Image */}
              <div className="relative aspect-[4/3] lg:aspect-auto">
                <img
                  src="https://customer-assets.emergentagent.com/job_ifotolife-popup/artifacts/iowf4yhl_59126EF2-77E7-4899-8ADB-7B8188C6CD42.png"
                  alt="Stand iFotoLife - Exemple d'installation"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-xs font-medium mb-4 w-fit">
                  <Building2 size={14} />
                  Stand Clé en Main
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Stand iFotoLife Complet
                </h3>
                <p className="text-white/60 mb-6 leading-relaxed">
                  Installation complète avec équipement professionnel, branding personnalisé et équipe de photographes iFotoLife sur place.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF7A00]" />
                    Borne tactile avec écran HD
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF7A00]" />
                    Appareil photo professionnel
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF7A00]" />
                    Équipe de photographes iFotoLife
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF7A00]" />
                    Branding personnalisé à vos couleurs
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="glass rounded-xl p-4 flex-1">
                    <div className="text-xs text-white/50 mb-1">Option Commission</div>
                    <div className="text-lg font-bold text-[#FF7A00]">20% reversé</div>
                    <div className="text-xs text-white/40">sur les ventes</div>
                  </div>
                  <div className="glass rounded-xl p-4 flex-1 border border-[#10B981]/30">
                    <div className="text-xs text-white/50 mb-1">Option Loyer</div>
                    <div className="text-lg font-bold text-[#10B981]">Loyer fixe</div>
                    <div className="text-xs text-white/40">payé par iFotoLife</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Solutions Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                data-testid={`solution-card-${index}`}
                className={`relative group h-full ${solution.highlight ? 'lg:-mt-4 lg:mb-4' : ''}`}
              >
                {/* Highlight Glow for first card */}
                {solution.highlight && (
                  <div className="absolute inset-0 bg-gradient-to-b from-[#FF7A00]/20 to-transparent rounded-3xl blur-xl opacity-50" />
                )}
                
                <div className={`relative h-full rounded-3xl p-8 md:p-10 transition-all duration-500 ${
                  solution.highlight 
                    ? 'bg-gradient-to-b from-[#FF7A00]/10 to-[#121212] border border-[#FF7A00]/30 hover:border-[#FF7A00]/50' 
                    : 'glass-card hover:border-[#FF7A00]/30'
                }`}>
                  {/* Popular Badge */}
                  {solution.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#FF7A00] text-white text-xs font-medium">
                      Populaire
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl mb-6 flex items-center justify-center ${
                    solution.highlight 
                      ? 'bg-[#FF7A00]/20 border border-[#FF7A00]/30' 
                      : 'bg-white/5 border border-white/10 group-hover:border-[#FF7A00]/30'
                  } transition-colors`}>
                    <Icon className={`w-8 h-8 ${solution.highlight ? 'text-[#FF7A00]' : 'text-white/70 group-hover:text-[#FF7A00]'} transition-colors`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-[#FF7A00] transition-colors">
                    {solution.title}
                  </h3>
                  <p className="text-white/60 mb-6 leading-relaxed">
                    {solution.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3 mb-8">
                    {solution.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-white/70">
                        <div className={`w-1.5 h-1.5 rounded-full ${solution.highlight ? 'bg-[#FF7A00]' : 'bg-white/40'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA Link */}
                  <Link 
                    to="/popup-photo"
                    className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${
                      solution.highlight ? 'text-[#FF7A00]' : 'text-white/60 hover:text-[#FF7A00]'
                    }`}
                  >
                    En savoir plus
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <Link
            to="/popup-photo"
            data-testid="solutions-cta"
            className="btn-primary inline-flex items-center gap-2"
          >
            Configurer votre installation
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Solutions;
